import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded' | 'card' | 'image';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
  count?: number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
  count = 1,
}) => {
  const baseClasses = 'bg-neutral-200 inline-block';

  const variantClasses = {
    text: 'w-full h-4 rounded',
    circular: 'rounded-full',
    rectangular: '',
    rounded: 'rounded-xl',
    card: 'rounded-3xl',
    image: 'rounded-xl',
  };

  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'skeleton-wave',
    none: '',
  };

  const style: React.CSSProperties = {
    width:
      width !== undefined
        ? typeof width === 'number'
          ? `${width}px`
          : width
        : undefined,
    height:
      height !== undefined
        ? typeof height === 'number'
          ? `${height}px`
          : height
        : undefined,
  };

  const items = Array.from({ length: count }, (_, index) => (
    <span
      key={index}
      className={`${baseClasses} ${variantClasses[variant]} ${animationClasses[animation]} ${className}`}
      style={style}
    />
  ));

  return <>{items}</>;
};

interface SkeletonTextProps {
  lines?: number;
  className?: string;
  lineHeight?: string | number;
  width?: string | number | string[];
  animation?: 'pulse' | 'wave' | 'none';
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  className = '',
  lineHeight = '1rem',
  width = '100%',
  animation = 'pulse',
}) => {
  const getWidth = (index: number): string | number => {
    if (Array.isArray(width)) {
      return width[index % width.length];
    }
    if (index === lines - 1 && typeof width === 'string' && width === '100%') {
      return '70%'; // Make last line shorter if width is 100%
    }
    return width;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          variant="text"
          width={getWidth(index)}
          height={lineHeight}
          animation={animation}
        />
      ))}
    </div>
  );
};

interface SkeletonCardProps {
  imageHeight?: string | number;
  lines?: number;
  className?: string;
  animation?: 'pulse' | 'wave' | 'none';
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  imageHeight = '12rem',
  lines = 3,
  className = '',
  animation = 'pulse',
}) => {
  return (
    <div className={`overflow-hidden ${className}`}>
      <Skeleton
        variant="image"
        width="100%"
        height={imageHeight}
        animation={animation}
      />
      <div className="p-4 space-y-4">
        <Skeleton
          variant="text"
          width="70%"
          height="1.5rem"
          animation={animation}
        />
        <SkeletonText
          lines={lines}
          lineHeight="0.875rem"
          animation={animation}
          width={['100%', '90%', '80%']}
        />
        <div className="flex justify-between items-center pt-2">
          <Skeleton
            variant="text"
            width="30%"
            height="1rem"
            animation={animation}
          />
          <Skeleton
            variant="rounded"
            width="5rem"
            height="2rem"
            animation={animation}
          />
        </div>
      </div>
    </div>
  );
};

interface SkeletonGridProps {
  count?: number;
  columns?: number;
  gap?: string;
  className?: string;
  children: React.ReactNode;
}

export const SkeletonGrid: React.FC<SkeletonGridProps> = ({
  count = 3,
  columns = 3,
  gap = 'gap-6',
  className = '',
  children,
}) => {
  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${columns} ${gap} ${className}`}
    >
      {Array.from({ length: count }, (_, index) => (
        <React.Fragment key={index}>{children}</React.Fragment>
      ))}
    </div>
  );
};

// Add wave animation to global styles
export const SkeletonStyles = () => (
  <style>{`
    @keyframes skeletonWave {
      0% {
        background-position: -200px 0;
      }
      100% {
        background-position: calc(200px + 100%) 0;
      }
    }
    
    .skeleton-wave {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200px 100%;
      animation: skeletonWave 1.5s infinite;
    }
  `}</style>
);
